import React, { useState, useEffect } from 'react';
import { ArrowUpRight, MessageCircle, Calendar, MapPin, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { track } from '../lib/analytics';
import Footer from '../components/Footer';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
    honeypot: '',
  });
  const [formStartTime, setFormStartTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });

  useEffect(() => {
    setFormStartTime(Date.now());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: null, message: '' });

    if (formData.honeypot !== '') return;
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 5000) {
      setStatus({ type: 'error', message: 'Please take a moment to fill the form properly.' });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    if (formData.message.length < 10) {
      setStatus({ type: 'error', message: 'Tell me a little more — at least 10 characters.' });
      return;
    }

    setIsSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          message: formData.message,
          honeypot: formData.honeypot,
          elapsedMs: timeTaken,
          source: 'contact',
        }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || 'Failed to send message');
      }
      setStatus({
        type: 'success',
        message: 'Thanks — I\'ll be in touch within 24 hours.',
      });
      track('contact_form_submit', { source: 'contact_page' });
      setFormData({ firstName: '', lastName: '', email: '', message: '', honeypot: '' });
      setFormStartTime(Date.now());
    } catch (err) {
      setStatus({ type: 'error', message: `Sorry, that didn't go through. ${err.message || ''}` });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <>
      <SEO
        title="Contact — Free Email Marketing Audit | Growth Hacking"
        description="Send me your numbers and I'll come back with three things I'd ship in your first week. Or book a 30-minute strategy call."
        canonical="/contact"
      />
      <main className="bg-cream pt-28" data-testid="contact-page">
        <section className="container-x section pt-12">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Left: copy + meta */}
            <div className="lg:col-span-5">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Contact
              </span>
              <h1 className="font-display text-5xl md:text-6xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                Tell me about your{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">inbox</span>.
              </h1>
              <p className="mt-6 text-lg text-muted-warm leading-relaxed">
                Send a brief, share your numbers, or book a strategy call. Either way you
                hear back from a real human (me) within a day.
              </p>

              <div className="mt-10 space-y-3">
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-cream-2 border border-line">
                  <span className="w-9 h-9 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center">
                    <Clock className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-muted-warm font-semibold">Response time</p>
                    <p className="text-sm font-semibold text-ink">Within 24 hours, Mon–Fri</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-cream-2 border border-line">
                  <span className="w-9 h-9 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center">
                    <MapPin className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-muted-warm font-semibold">Working hours</p>
                    <p className="text-sm font-semibold text-ink">9 AM – 6 PM GMT · async friendly</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-cream-2 border border-line">
                  <span className="w-9 h-9 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center">
                    <MessageCircle className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-widest text-muted-warm font-semibold">Audit inquiries</p>
                    <p className="text-sm font-semibold text-ink">Free 30-min call · no deck</p>
                  </div>
                </div>
              </div>

              <a
                href="https://tidycal.com/akashtiwari/30-minute-meeting"
                target="_blank" rel="noopener noreferrer"
                data-testid="contact-book-call-btn"
                className="btn-ink mt-10"
              >
                <Calendar className="w-4 h-4" /> Book a 30-minute call
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              <div className="rounded-[1.75rem] bg-cream-2 border border-line p-8 md:p-10">
                <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
                  Send a brief
                </p>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-3 text-ink leading-tight">
                  What are you trying to build?
                </h2>

                <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">First name</label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="Akash"
                        data-testid="contact-first-name"
                        className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">Last name</label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Tiwari"
                        data-testid="contact-last-name"
                        className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">Work email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@brand.com"
                      data-testid="contact-email"
                      className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">Tell me about your list</label>
                    <textarea
                      name="message"
                      required
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="ESP · list size · last-30-day open rate · what you'd like to fix or build…"
                      data-testid="contact-message"
                      className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all resize-none text-ink"
                    />
                  </div>

                  {/* honeypot */}
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />

                  {status.type === 'error' && (
                    <p className="text-sm text-red-600" data-testid="contact-error">{status.message}</p>
                  )}
                  {status.type === 'success' && (
                    <p className="text-sm text-[var(--gh-orange)] font-semibold" data-testid="contact-success">{status.message}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="btn-orange w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending…' : 'Send brief'}
                    {!isSubmitting && <ArrowUpRight className="w-4 h-4" />}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Contact;
